/**
 * ModalReportarOlvido.js
 * Modal para reportar que el usuario olvidó tomar medicamentos
 * Muestra checkboxes con los bloques horarios del día
 */
import React, { useState } from 'react';
import { reportarOlvido } from '../services/api';
import toast from 'react-hot-toast';

// Mapeo de bloques a iconos y nombres amigables
const BLOQUES_INFO = {
  madrugada: { icon: '🌙', nombre: 'Madrugada', hora: '6:00 AM' },
  mañana: { icon: '🌅', nombre: 'Mañana', hora: '9:00 AM' },
  mediodía: { icon: '☀️', nombre: 'Mediodía', hora: '3:00 PM' },
  tarde: { icon: '🌇', nombre: 'Tarde', hora: '5:00 PM' },
  noche: { icon: '🌙', nombre: 'Noche', hora: '9:00 PM' },
  nocturno: { icon: '🌑', nombre: 'Nocturno', hora: '2:00 AM' }
};

export default function ModalReportarOlvido({
  show,
  onClose,
  tratamientoId,
  bloquesDisponibles,
  onSuccess
}) {
  const [bloquesSeleccionados, setBloquesSeleccionados] = useState([]);
  const [saving, setSaving] = useState(false);

  if (!show) return null;

  function toggleBloque(bloque) {
    setBloquesSeleccionados(prev => {
      if (prev.includes(bloque)) {
        return prev.filter(b => b !== bloque);
      } else {
        return [...prev, bloque];
      }
    });
  }

  async function handleGuardar() {
    if (bloquesSeleccionados.length === 0) {
      toast.error('Selecciona al menos un horario');
      return;
    }

    setSaving(true);

    // Obtener fecha de hoy en formato YYYY-MM-DD
    const hoy = new Date().toISOString().split('T')[0];

    const result = await reportarOlvido(tratamientoId, hoy, bloquesSeleccionados);

    setSaving(false);

    if (result.error) {
      toast.error(result.error);
      return;
    }

    // Mostrar mensaje según si se mantuvo la racha
    if (result.rachaMantenida) {
      toast.success(result.mensaje || '¡No pasa nada, mañana será mejor!');
    } else {
      toast(result.mensaje || 'Tu racha se reinició, pero puedes volver a empezar', {
        icon: '🔄',
        duration: 4000
      });
    }

    setBloquesSeleccionados([]);

    if (onSuccess) {
      onSuccess(result);
    }

    onClose();
  }

  function handleClose() {
    setBloquesSeleccionados([]);
    onClose();
  }

  // Ordenar bloques de manera lógica
  const ordenBloques = ['madrugada', 'mañana', 'mediodía', 'tarde', 'noche', 'nocturno'];
  const bloquesOrdenados = bloquesDisponibles.sort((a, b) => {
    return ordenBloques.indexOf(a) - ordenBloques.indexOf(b);
  });

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100001,
        padding: '16px'
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '20px',
          maxWidth: '400px',
          width: '100%',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 24px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>😅</div>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#111' }}>
            ¿Qué toma olvidaste?
          </h3>
          <p style={{ margin: '8px 0 0', color: '#666', fontSize: '14px' }}>
            Selecciona los horarios que olvidaste
          </p>
        </div>

        {/* Lista de bloques */}
        <div style={{ padding: '0 24px 16px' }}>
          {bloquesOrdenados.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '24px',
                color: '#666'
              }}
            >
              No hay recordatorios para hoy
            </div>
          ) : (
            bloquesOrdenados.map((bloque) => {
              const info = BLOQUES_INFO[bloque] || { icon: '⏰', nombre: bloque, hora: '' };
              const isSelected = bloquesSeleccionados.includes(bloque);

              return (
                <label
                  key={bloque}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    backgroundColor: isSelected ? '#FFEBEE' : '#f9fafb',
                    borderRadius: '12px',
                    marginBottom: '8px',
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #FF5722' : '2px solid transparent',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleBloque(bloque)}
                    style={{
                      width: '20px',
                      height: '20px',
                      accentColor: '#FF5722'
                    }}
                  />
                  <span style={{ fontSize: '24px' }}>{info.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', color: '#111' }}>{info.nombre}</div>
                    {info.hora && (
                      <div style={{ fontSize: '13px', color: '#666' }}>{info.hora}</div>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>

        {/* Mensaje motivacional */}
        <div
          style={{
            padding: '16px 24px',
            backgroundColor: '#f0fdf4',
            borderTop: '1px solid #bbf7d0'
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: '13px',
              color: '#166534',
              textAlign: 'center'
            }}
          >
            💪 No te preocupes, todos tenemos días difíciles. ¡Mañana puedes lograrlo!
          </p>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            display: 'flex',
            gap: '12px',
            borderTop: '1px solid #e5e7eb'
          }}
        >
          <button
            onClick={handleClose}
            disabled={saving}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #d1d5db',
              borderRadius: '10px',
              backgroundColor: '#fff',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleGuardar}
            disabled={saving || bloquesSeleccionados.length === 0}
            style={{
              flex: 1,
              padding: '12px',
              border: 'none',
              borderRadius: '10px',
              backgroundColor: saving || bloquesSeleccionados.length === 0 ? '#d1d5db' : '#FF5722',
              color: '#fff',
              cursor: saving || bloquesSeleccionados.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status"></span>
                Guardando...
              </>
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
